import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { useAuth } from '../hooks/useAuth';
import {
  LoginPayload,
  RegisterPayload,
  register as registerAPI,
  sendPasswordResetEmail,
} from '../services/authService';

const getPostLoginRoute = (role?: string) => {
  if (role === 'ADMIN') return '/admin';
  return '/console';
};

const trustPoints = [
  'Remapped, HLS, HLS+, pull, and direct output delivery.',
  'Live ingest oversight with operator-grade monitoring.',
  'Fast channel provisioning after account review.',
];

const setupSteps = [
  'Your broadcaster account is submitted for admin review.',
  'Your first channel is provisioned under the primary channel name.',
  'After approval, you sign in and configure destinations.',
];

const signupCountries = [
  'Afghanistan',
  'Albania',
  'Algeria',
  'Andorra',
  'Angola',
  'Antigua and Barbuda',
  'Argentina',
  'Armenia',
  'Australia',
  'Austria',
  'Azerbaijan',
  'Bahamas',
  'Bahrain',
  'Bangladesh',
  'Barbados',
  'Belarus',
  'Belgium',
  'Belize',
  'Benin',
  'Bhutan',
  'Bolivia',
  'Bosnia and Herzegovina',
  'Botswana',
  'Brazil',
  'Brunei',
  'Bulgaria',
  'Burkina Faso',
  'Burundi',
  'Cabo Verde',
  'Cambodia',
  'Cameroon',
  'Canada',
  'Central African Republic',
  'Chad',
  'Chile',
  'China',
  'Colombia',
  'Comoros',
  'Congo',
  'Costa Rica',
  "Cote d'Ivoire",
  'Croatia',
  'Cuba',
  'Cyprus',
  'Czechia',
  'Democratic Republic of the Congo',
  'Denmark',
  'Djibouti',
  'Dominica',
  'Dominican Republic',
  'Ecuador',
  'Egypt',
  'El Salvador',
  'Equatorial Guinea',
  'Eritrea',
  'Estonia',
  'Eswatini',
  'Ethiopia',
  'Fiji',
  'Finland',
  'France',
  'Gabon',
  'Gambia',
  'Georgia',
  'Germany',
  'Ghana',
  'Greece',
  'Grenada',
  'Guatemala',
  'Guinea',
  'Guinea-Bissau',
  'Guyana',
  'Haiti',
  'Honduras',
  'Hungary',
  'Iceland',
  'India',
  'Indonesia',
  'Iran',
  'Iraq',
  'Ireland',
  'Israel',
  'Italy',
  'Jamaica',
  'Japan',
  'Jordan',
  'Kazakhstan',
  'Kenya',
  'Kiribati',
  'Kuwait',
  'Kyrgyzstan',
  'Laos',
  'Latvia',
  'Lebanon',
  'Lesotho',
  'Liberia',
  'Libya',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Madagascar',
  'Malawi',
  'Malaysia',
  'Maldives',
  'Mali',
  'Malta',
  'Marshall Islands',
  'Mauritania',
  'Mauritius',
  'Mexico',
  'Micronesia',
  'Moldova',
  'Monaco',
  'Mongolia',
  'Montenegro',
  'Morocco',
  'Mozambique',
  'Myanmar',
  'Namibia',
  'Nauru',
  'Nepal',
  'Netherlands',
  'New Zealand',
  'Nicaragua',
  'Niger',
  'Nigeria',
  'North Korea',
  'North Macedonia',
  'Norway',
  'Oman',
  'Pakistan',
  'Palau',
  'Palestine',
  'Panama',
  'Papua New Guinea',
  'Paraguay',
  'Peru',
  'Philippines',
  'Poland',
  'Portugal',
  'Qatar',
  'Romania',
  'Russia',
  'Rwanda',
  'Saint Kitts and Nevis',
  'Saint Lucia',
  'Saint Vincent and the Grenadines',
  'Samoa',
  'San Marino',
  'Sao Tome and Principe',
  'Saudi Arabia',
  'Senegal',
  'Serbia',
  'Seychelles',
  'Sierra Leone',
  'Singapore',
  'Slovakia',
  'Slovenia',
  'Solomon Islands',
  'Somalia',
  'South Africa',
  'South Korea',
  'South Sudan',
  'Spain',
  'Sri Lanka',
  'Sudan',
  'Suriname',
  'Sweden',
  'Switzerland',
  'Syria',
  'Taiwan',
  'Tajikistan',
  'Tanzania',
  'Thailand',
  'Timor-Leste',
  'Togo',
  'Tonga',
  'Trinidad and Tobago',
  'Tunisia',
  'Turkey',
  'Turkmenistan',
  'Tuvalu',
  'Uganda',
  'Ukraine',
  'United Arab Emirates',
  'United Kingdom',
  'United States',
  'Uruguay',
  'Uzbekistan',
  'Vanuatu',
  'Vatican City',
  'Venezuela',
  'Vietnam',
  'Yemen',
  'Zambia',
  'Zimbabwe',
];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
  },
};

export const AuthPage: React.FC = () => {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const { login, isLoggedIn, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoggedIn && user) {
      navigate(getPostLoginRoute(user.role), { replace: true });
    }
  }, [isLoggedIn, user, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: { xs: 3, md: 5 },
        px: 2,
        background:
          'radial-gradient(circle at top right, var(--secondary-light), transparent 26%), radial-gradient(circle at bottom left, var(--primary-light), transparent 28%), linear-gradient(180deg, var(--bg-body) 0%, color-mix(in srgb, var(--bg-body) 92%, var(--bg-card) 8%) 100%)',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={0} sx={{ minHeight: { md: 'calc(100vh - 80px)' } }}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Box
              sx={{
                height: '100%',
                p: { xs: 3, md: 5 },
                pr: { md: 4 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'relative',
                overflow: 'hidden',
                border: '1px solid var(--border)',
                borderRight: { md: 'none' },
                borderRadius: { xs: 3, md: '24px 0 0 24px' },
                background:
                  'linear-gradient(160deg, color-mix(in srgb, var(--bg-card) 82%, var(--bg-card-hover) 18%) 0%, color-mix(in srgb, var(--bg-card) 96%, var(--bg-body) 4%) 100%)',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0.16,
                  backgroundImage:
                    'linear-gradient(to right, transparent 0, transparent calc(100% - 1px), var(--border) calc(100% - 1px)), linear-gradient(to bottom, transparent 0, transparent calc(100% - 1px), var(--border) calc(100% - 1px))',
                  backgroundSize: '48px 48px',
                  pointerEvents: 'none',
                }}
              />

              <Stack spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
                <Box>
                  <Typography variant="overline" sx={{ color: 'secondary.main' }}>
                    Streamy Control Plane
                  </Typography>
                  <Typography variant="h2" sx={{ mt: 1, maxWidth: 420 }}>
                    Launch and monitor your live channels without guesswork.
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 2, maxWidth: 430, color: 'text.secondary' }}>
                    Streamy is built for broadcasters who need clean delivery, controlled capacity, and a live operations view that stays honest.
                  </Typography>
                </Box>

                <Stack spacing={1.5}>
                  {trustPoints.map((point) => (
                    <Box key={point} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Box
                        sx={{
                          mt: '7px',
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          flexShrink: 0,
                          background: 'linear-gradient(135deg, var(--secondary), var(--primary))',
                        }}
                      />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {point}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Stack>

              <Paper
                variant="outlined"
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  mt: { xs: 2, md: 0 },
                  p: 2.5,
                  borderRadius: 3,
                  backgroundColor: 'color-mix(in srgb, var(--bg-card) 82%, transparent 18%)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Typography variant="overline">How Signup Works</Typography>
                <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                  {setupSteps.map((step, index) => (
                    <Box key={step} sx={{ display: 'flex', gap: 1.5 }}>
                      <Box
                        sx={{
                          minWidth: 26,
                          height: 26,
                          borderRadius: '50%',
                          border: '1px solid var(--border-strong)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'secondary.main',
                          fontSize: '0.74rem',
                          fontWeight: 700,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', pt: 0.25 }}>
                        {step}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Paper
              elevation={0}
              sx={(theme) => ({
                height: '100%',
                p: { xs: 3, sm: 4, md: 5 },
                borderRadius: { xs: 3, md: '0 24px 24px 0' },
                border: '1px solid var(--border)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 20px 50px rgba(0,0,0,0.45)'
                  : '0 20px 50px rgba(15,23,42,0.08)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              })}
            >
              <Stack spacing={3}>
                <Box>
                  <Typography variant="overline" sx={{ color: 'primary.main' }}>
                    Account Access
                  </Typography>
                  <Typography variant="h3" sx={{ mt: 1 }}>
                    {mode === 'login' ? 'Sign in to manage your live channels' : 'Create your broadcaster account'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', maxWidth: 540 }}>
                    {mode === 'login'
                      ? 'Access your console, review delivery health, and manage outputs from one operational workspace.'
                      : 'Submit your details, reserve your primary channel name, and enter the review queue for provisioning.'}
                  </Typography>
                </Box>

                <Tabs
                  value={mode}
                  onChange={(_, value: 'login' | 'register') => setMode(value)}
                  sx={{
                    minHeight: 46,
                    borderRadius: 2.5,
                    backgroundColor: 'var(--bg-input)',
                    '& .MuiTabs-flexContainer': { gap: 0.5, p: 0.5 },
                    '& .MuiTabs-indicator': { display: 'none' },
                  }}
                >
                  <Tab
                    disableRipple
                    value="login"
                    label="Sign In"
                    sx={{
                      borderRadius: 2,
                      minHeight: 34,
                      flex: 1,
                      textTransform: 'none',
                      fontWeight: 600,
                      transition: 'background-color 120ms ease, color 120ms ease',
                      '&.Mui-selected': {
                        backgroundColor: 'var(--bg-card)',
                        color: 'text.primary',
                        boxShadow: '0 1px 2px rgba(15,23,42,0.12)',
                      },
                    }}
                  />
                  <Tab
                    disableRipple
                    value="register"
                    label="Create Account"
                    sx={{
                      borderRadius: 2,
                      minHeight: 34,
                      flex: 1,
                      textTransform: 'none',
                      fontWeight: 600,
                      transition: 'background-color 120ms ease, color 120ms ease',
                      '&.Mui-selected': {
                        backgroundColor: 'var(--bg-card)',
                        color: 'text.primary',
                        boxShadow: '0 1px 2px rgba(15,23,42,0.12)',
                      },
                    }}
                  />
                </Tabs>

                {mode === 'login' ? (
                  <LoginForm onLogin={login} onSwitchToRegister={() => setMode('register')} />
                ) : (
                  <RegisterForm
                    onRegisterSuccess={() => setMode('login')}
                    onSwitchToLogin={() => setMode('login')}
                  />
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

const LoginForm: React.FC<{
  onLogin: (payload: LoginPayload) => Promise<void>;
  onSwitchToRegister: () => void;
}> = ({ onLogin, onSwitchToRegister }) => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await onLogin({ email: form.email.trim(), password: form.password });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2.25}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            required
            fullWidth
            autoFocus
            label="Email Address"
            name="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            helperText="Use the email approved for your broadcaster or admin account."
            sx={fieldSx}
          />

          <TextField
            required
            fullWidth
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            helperText="Passwords are case-sensitive."
            sx={fieldSx}
            slotProps={{
              input: {
                endAdornment: (
                  <Button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    size="small"
                    sx={{ minWidth: 0, px: 1, mr: -0.5, color: 'text.secondary', borderRadius: 1.5, textTransform: 'none' }}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </Button>
                ),
              },
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Link component="button" type="button" variant="body2" onClick={() => setShowRecovery(true)}>
              Forgot your password?
            </Link>
            <Typography variant="caption">Accounts under review cannot sign in yet.</Typography>
          </Box>

          <Button type="submit" fullWidth variant="contained" disableElevation size="large" disabled={loading} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
          </Button>
        </Stack>
      </Box>

      <Divider />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Need a broadcaster account?
        </Typography>
        <Button variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }} onClick={onSwitchToRegister}>
          Create Account
        </Button>
      </Box>

      <PasswordRecoveryDialog open={showRecovery} onClose={() => setShowRecovery(false)} initialEmail={form.email} />
    </>
  );
};

const RegisterForm: React.FC<{
  onRegisterSuccess: () => void;
  onSwitchToLogin: () => void;
}> = ({ onRegisterSuccess, onSwitchToLogin }) => {
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    phone: '+254',
    country: 'Kenya',
    streamName: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const validatePhone = (phone: string) => /^\+\d{7,15}$/.test(phone);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const phoneError = phoneTouched && form.phone.length > 1 && !validatePhone(form.phone);
  const normalizedStreamName = useMemo(() => form.streamName.replace(/\s/g, ''), [form.streamName]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (form.password.length < 12 || form.password.length > 256) {
      setError('Use a password between 12 and 256 characters.');
      return;
    }

    if (!validatePhone(form.phone)) {
      setError('Phone number must use international format, for example +254xxxxxxxxx.');
      return;
    }

    if (!form.country) {
      setError('Country is required.');
      return;
    }

    if (!normalizedStreamName) {
      setError('Primary channel name is required.');
      return;
    }

    setLoading(true);

    try {
      const payload: RegisterPayload = {
        email: form.email.trim(),
        password: form.password,
        phone: form.phone.trim(),
        country: form.country,
        streamName: normalizedStreamName.trim(),
        role: 'BROADCASTER',
      };

      await registerAPI(payload);
      setSuccess('Application received. Your account is now awaiting admin review.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: 3,
          background: 'linear-gradient(180deg, var(--success-light) 0%, transparent 100%)',
        }}
      >
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 28 }} />
            <Typography variant="h5" sx={{ lineHeight: 1.2 }}>Application received</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Your account has been created and queued for admin review. You will be able to sign in after approval and provisioning.
          </Typography>
          <Box
            sx={(theme) => ({
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              px: 2,
              py: 1.5,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
            })}
          >
            <Stack spacing={0.5}>
              {[
                ['Email', form.email],
                ['Primary channel', normalizedStreamName],
                ['Contact', form.phone],
                ['Country', form.country],
              ].map(([label, value]) => (
                <Box key={label} sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 120 }}>{label}</Typography>
                  <Typography variant="body2" fontWeight={500}>{value}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button variant="contained" disableElevation sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }} onClick={onRegisterSuccess}>
              Return to Sign In
            </Button>
            <Button variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }} onClick={onSwitchToLogin}>
              Back to Login
            </Button>
          </Box>
        </Stack>
      </Paper>
    );
  }

  return (
    <>
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box>
            <Typography variant="subtitle1">Account</Typography>
            <Typography variant="caption">These details are used for sign-in and review approval.</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                label="Email Address"
                autoComplete="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                sx={fieldSx}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                label="Phone Number"
                value={form.phone}
                error={!!phoneError}
                helperText={phoneError ? 'Use international format, e.g. +254700000000' : 'Use international format, e.g. +254xxxxxxxxx'}
                onBlur={() => setPhoneTouched(true)}
                onChange={(e) => {
                  // Allow only a leading + followed by digits
                  const raw = e.target.value;
                  const filtered = raw.startsWith('+')
                    ? '+' + raw.slice(1).replace(/\D/g, '')
                    : raw.replace(/\D/g, '');
                  setForm({ ...form, phone: filtered });
                }}
                sx={fieldSx}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                label="Password"
                type="password"
                autoComplete="new-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                sx={fieldSx}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                label="Confirm Password"
                type="password"
                autoComplete="new-password"
                value={form.confirmPassword}
                onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                sx={fieldSx}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                required
                fullWidth
                label="Country"
                value={form.country}
                helperText="Used for billing currency"
                autoComplete="country-name"
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                sx={fieldSx}
              >
                {signupCountries.map((country) => (
                  <MenuItem key={country} value={country}>{country}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Divider />

          <Box>
            <Typography variant="subtitle1">Channel Setup</Typography>
            <Typography variant="caption">This becomes the first channel provisioned on your account.</Typography>
          </Box>

          <TextField
            required
            fullWidth
            label="Primary Channel Name"
            helperText="Use letters and numbers only. Spaces are removed automatically."
            value={form.streamName}
            onChange={(e) => setForm({ ...form, streamName: e.target.value.replace(/\s/g, '') })}
            sx={fieldSx}
          />

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, backgroundColor: 'var(--bg-input)' }}>
            <Typography variant="subtitle2">Before you submit</Typography>
            <Stack spacing={0.75} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Admin review is required before first sign-in.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Your primary channel name should be stable and production-safe.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                After approval, you can add pull, push, HLS, and direct outputs from the console.
              </Typography>
            </Stack>
          </Paper>

          <Button type="submit" fullWidth variant="contained" disableElevation size="large" disabled={loading} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Submit for Review'}
          </Button>
        </Stack>
      </Box>

      <Divider />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Already approved and ready to sign in?
        </Typography>
        <Button variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }} onClick={onSwitchToLogin}>
          Back to Login
        </Button>
      </Box>
    </>
  );
};

const PasswordRecoveryDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  initialEmail: string;
}> = ({ open, onClose, initialEmail }) => {
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setEmail(initialEmail);
      setMessage('');
      setError('');
    }
  }, [open, initialEmail]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    try {
      const response = await sendPasswordResetEmail(email.trim());
      setMessage(response.message || 'Password reset link has been sent to that email address.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not start password recovery.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: (theme: any) => ({
          borderRadius: 3,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 24px 60px rgba(0,0,0,0.55)'
            : '0 24px 60px rgba(15,23,42,0.16)',
        }),
      }}
    >
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle>Password Recovery</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Enter the email tied to your account. Streamy will tell you if the account is not found.
            </Typography>
            {message && <Alert severity="success">{message}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              required
              fullWidth
              autoFocus
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={fieldSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
          <Button type="submit" variant="contained" disableElevation disabled={loading} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Send Reset Link'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
